import { ChangeDetectionStrategy, Component, TemplateRef, contentChild, input, output } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectButtonModule } from 'primeng/selectbutton';
import { PaginatorModule } from 'primeng/paginator';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';

export type EntityListViewMode = 'grid' | 'list';

/**
 * Generic shell for "card grid / row list" entity browsing with pagination —
 * the pattern introduced by the monitoring stage browser, generalised so any
 * feature (simulations, and future lists) can reuse the view-mode toggle and
 * paging chrome without re-implementing them. The shell owns no data: it
 * projects a card template (rendered in a responsive grid) and a row template
 * (rendered inside one bordered list container), and the host owns fetching
 * and page state, exactly like DataTableComponent's [first]/[rows]/[totalRecords]
 * contract.
 *
 * Usage:
 * ```html
 * <cosmos-paginated-entity-list
 *   [items]="simulations()"
 *   [totalRecords]="totalRecords()"
 *   [rows]="rows()"
 *   [first]="first()"
 *   [viewMode]="viewMode()"
 *   (viewModeChange)="setViewMode($event)"
 *   (pageChange)="onPageChange($event)">
 *   <ng-template #cardTemplate let-item>
 *     <cosmos-simulation-card [simulation]="item" />
 *   </ng-template>
 *   <ng-template #rowTemplate let-item let-first="first" let-last="last">
 *     <cosmos-simulation-row [simulation]="item" [isFirst]="first" [isLast]="last" />
 *   </ng-template>
 * </cosmos-paginated-entity-list>
 * ```
 */
@Component({
  selector: 'cosmos-paginated-entity-list',
  imports: [NgTemplateOutlet, FormsModule, SelectButtonModule, PaginatorModule, CardModule, SkeletonModule],
  templateUrl: './paginated-entity-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    /* The card owns the outer border; rows are divided by the container's
       divide-y (so there is no last-child border to strip). */
    .paginated-entity-list-list ::ng-deep .p-card-body { padding: 0.25rem; }
    .paginated-entity-list-list ::ng-deep .p-card-content { padding: 0; }
  `,
})
export class PaginatedEntityListComponent<T> {
  readonly items = input<T[]>([]);
  readonly loading = input<boolean>(false);
  readonly totalRecords = input<number>(0);
  readonly rows = input<number>(10);
  readonly first = input<number>(0);
  readonly rowsPerPageOptions = input<number[]>([10, 25, 50]);
  readonly trackBy = input<(item: T) => string | number | undefined>(() => undefined);

  readonly viewMode = input.required<EntityListViewMode>();
  readonly viewModeChange = output<EntityListViewMode>();
  readonly pageChange = output<{ first: number; rows: number }>();

  readonly cardTemplate = contentChild.required<TemplateRef<{ $implicit: T }>>('cardTemplate');
  readonly rowTemplate = contentChild.required<TemplateRef<{ $implicit: T; first: boolean; last: boolean }>>('rowTemplate');

  protected readonly viewModeOptions = [
    { icon: 'fa-solid fa-grip', value: 'grid' as const },
    { icon: 'fa-solid fa-list', value: 'list' as const },
  ];

  protected onViewModeChange(mode: EntityListViewMode): void {
    this.viewModeChange.emit(mode);
  }

  protected onPageChange(event: { first?: number; rows?: number }): void {
    this.pageChange.emit({ first: event.first ?? 0, rows: event.rows ?? this.rows() });
  }

  protected keyOf(item: T, index: number): string | number {
    return this.trackBy()(item) ?? index;
  }
}
